import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import {
  Monitor,
  Landmark,
  Building,
  TrendingUp,
  Users,
  Briefcase,
  ArrowRight,
  CheckCircle,
  MapPin,
} from "lucide-react";

const IndustriesPage = () => {
  const openCalendly = () => {
    if (window.Calendly) {
      window.Calendly.initPopupWidget({
        url: "https://calendly.com/franjo-vucic-d0pg/30min",
      });
    }
  };

  const industries = [
    {
      icon: Monitor,
      title: "Technology Companies",
      description: "Corporate structuring, operational infrastructure and compliance support for technology companies establishing operations in Bosnia and Herzegovina.",
      services: [
        "Company formation and structuring",
        "Intellectual property protection",
        "Employment and HR setup",
        "Tax optimization",
        "Operational infrastructure",
      ],
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop",
    },
    {
      icon: Landmark,
      title: "Fintech & Financial Services",
      description: "Regulatory coordination, corporate setup and compliance infrastructure for financial services companies entering the Bosnian market.",
      services: [
        "Regulatory licensing support",
        "Corporate structuring",
        "Compliance frameworks",
        "Local representation",
        "Ongoing administration",
      ],
      image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=600&h=400&fit=crop",
    },
    {
      icon: Building,
      title: "Hospitality & Platform Businesses",
      description: "Operational support for hospitality groups and platform businesses establishing presence in Bosnia and Herzegovina.",
      services: [
        "Local entity setup",
        "Employment structures",
        "Vendor management",
        "Regulatory compliance",
        "Financial administration",
      ],
      image: "https://images.unsplash.com/photo-1558959357-685f9c7ace7b?w=600&h=400&fit=crop",
    },
    {
      icon: TrendingUp,
      title: "International Investors",
      description: "Investment structuring, due diligence support and ongoing administration for investors targeting opportunities in Bosnia and Herzegovina.",
      services: [
        "Investment vehicle structuring",
        "Due diligence coordination",
        "Transaction support",
        "Post-acquisition integration",
        "Portfolio administration",
      ],
      image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&h=400&fit=crop",
    },
    {
      icon: Users,
      title: "Family Offices",
      description: "Comprehensive family office services for high-net-worth individuals with investments or interests in Bosnia and Herzegovina.",
      services: [
        "Investment coordination",
        "Corporate administration",
        "Asset protection structures",
        "Residence planning",
        "Succession planning",
      ],
      image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&h=400&fit=crop",
    },
    {
      icon: Briefcase,
      title: "Professional Services Firms",
      description: "Administrative support and corporate infrastructure for consulting, legal and accounting firms operating in Bosnia and Herzegovina.",
      services: [
        "Local entity formation",
        "Office establishment",
        "Employment structures",
        "Regulatory compliance",
        "Back office administration",
      ],
      image: "https://images.unsplash.com/photo-1497215728101-856f4ea42174?w=600&h=400&fit=crop",
    },
  ];

  return (
    <div data-testid="industries-page" className="pt-16 md:pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-[#F5F7FA] to-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-5 h-5 text-[#2F6FED]" />
              <p className="text-sm text-[#2F6FED] uppercase tracking-widest font-semibold">
                Bosnia and Herzegovina
              </p>
            </div>
            <h1 className="text-4xl md:text-5xl font-semibold tracking-tight leading-[1.1] text-[#0B2A4A] mb-6 font-['Sora']">
              Industries We Serve
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-slate-600">
              VTZ Backoffice provides specialized corporate and operational services 
              for international companies across various industries entering Bosnia and Herzegovina.
            </p>
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {industries.map((industry, index) => (
              <div
                key={index}
                data-testid={`industry-card-${index}`}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? "" : ""
                }`}
              >
                {/* Image */}
                <div className={`relative ${index % 2 === 1 ? "lg:order-2" : ""}`}>
                  <div className="relative rounded-xl overflow-hidden shadow-lg">
                    <img
                      src={industry.image}
                      alt={industry.title}
                      className="w-full h-[300px] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B2A4A]/60 to-transparent"></div>
                    <div className="absolute bottom-6 left-6 flex items-center gap-3">
                      <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                        <industry.icon className="w-6 h-6 text-[#2F6FED]" />
                      </div>
                      <span className="text-white font-semibold text-lg">{industry.title}</span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className={index % 2 === 1 ? "lg:order-1" : ""}>
                  <h2 className="text-2xl md:text-3xl font-semibold text-[#0B2A4A] mb-4 font-['Sora']">
                    {industry.title}
                  </h2>
                  <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                    {industry.description}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {industry.services.map((service, sIndex) => (
                      <li key={sIndex} className="flex items-center text-slate-600">
                        <CheckCircle className="w-5 h-5 text-[#2F6FED] mr-3 flex-shrink-0" />
                        {service}
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={openCalendly}
                    className="bg-[#2F6FED] hover:bg-[#2558be] text-white font-medium px-6 py-3 rounded-md transition-all duration-200"
                  >
                    Discuss Your Requirements
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Doing Business Guide CTA */}
      <section className="py-16 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl p-8 md:p-12 shadow-sm border border-slate-100">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-semibold text-[#0B2A4A] mb-4 font-['Sora']">
                  New to Bosnia and Herzegovina?
                </h2>
                <p className="text-slate-600 mb-6">
                  Read our comprehensive guide covering company formation, tax environment, 
                  employment requirements and operational considerations.
                </p>
                <Link to="/doing-business-bosnia">
                  <Button className="bg-[#0B2A4A] hover:bg-[#0B2A4A]/90 text-white font-medium px-6 py-3 rounded-md">
                    Doing Business in BiH Guide
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
              <div className="hidden lg:block text-right">
                <div className="inline-flex items-center gap-3 bg-[#F5F7FA] rounded-xl px-6 py-4">
                  <MapPin className="w-8 h-8 text-[#2F6FED]" />
                  <div className="text-left">
                    <p className="font-semibold text-[#0B2A4A]">Bosnia and Herzegovina</p>
                    <p className="text-sm text-slate-500">Gateway to Western Balkans</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-[#0B2A4A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-6 font-['Sora']">
            Don't See Your Industry?
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            We work with businesses across various sectors. Contact us to discuss 
            how our services can support your entry into Bosnia and Herzegovina.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              data-testid="industries-cta-book-call"
              onClick={openCalendly}
              className="bg-[#2F6FED] hover:bg-[#2558be] text-white font-medium px-8 py-4 rounded-md transition-all duration-200 shadow-lg"
            >
              Schedule a Consultation
            </Button>
            <Link to="/services">
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#0B2A4A] font-medium px-8 py-4 rounded-md transition-all duration-200"
              >
                View All Services
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IndustriesPage;
